import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { api, useFetch } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Seo, Loading, Empty } from "../components/Common";
import { OrderView } from "./Account";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export const ContentPage = ({ slug }) => {
  const { t, lang } = useI18n();
  const { data, loading, error } = useFetch(slug === "__404" ? null : `/content/${slug}?lang=${lang}`);
  if (slug === "__404" || error) return <div className="container-hw"><Seo title={t("not_found")} noindex /><Empty title={t("not_found")} action={<Link to="/" className="btn-primary">{t("home")}</Link>} /></div>;
  if (loading || !data) return <Loading />;
  return (
    <article className="container-hw py-12 max-w-3xl" data-testid={`content-page-${slug}`}>
      <Seo title={data.title} description={data.body.slice(0, 155)} />
      <h1 className="text-4xl sm:text-5xl mb-6">{data.title}</h1>
      {data.review_note && <p className="text-xs bg-[color:var(--hw-sale-l)] text-[color:var(--hw-sale)] p-3 mb-6" data-testid="legal-review-note">{t("review_note")}: {data.review_note}</p>}
      <div className="text-[15px] leading-relaxed whitespace-pre-line">{data.body}</div>
      <p className="text-xs text-[color:var(--hw-muted)] mt-10">{fmtDate(data.updated_at, lang)}</p>
    </article>
  );
};

export const Guides = () => {
  const { t, lang } = useI18n();
  const { data } = useFetch(`/guides?lang=${lang}`);
  return (
    <div className="container-hw py-12" data-testid="guides-page">
      <Seo title={t("guides")} description={t("guides")} />
      <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-10">{t("guides")}</h1>
      {!data ? <Loading /> : <div className="grid sm:grid-cols-2 gap-10">{data.map((g) => (
        <Link key={g.slug} to={`/inspiration/${g.slug}`} className="group" data-testid={`guide-${g.slug}`}><div className="aspect-[16/10] overflow-hidden mb-4"><img src={g.image} alt={g.title} loading="lazy" className="w-full h-full object-cover img-zoom" /></div><h2 className="text-3xl mb-2 group-hover:underline">{g.title}</h2><p className="text-sm text-[color:var(--hw-muted)]">{g.excerpt}</p></Link>
      ))}</div>}
    </div>
  );
};

export const Guide = () => {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const { data, error } = useFetch(`/content/${slug}?lang=${lang}`);
  if (error) return <Empty title={t("not_found")} />;
  if (!data) return <Loading />;
  return (
    <article className="pb-16" data-testid="guide-article">
      <Seo title={data.title} description={data.body.slice(0, 155)} image={data.image} jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: data.title, image: data.image, dateModified: data.updated_at }} />
      <div className="h-[46vh] overflow-hidden"><img src={data.image} alt={data.title} className="w-full h-full object-cover" /></div>
      <div className="container-hw max-w-3xl -mt-16 relative bg-[color:var(--hw-bg)] pt-10">
        <Link to="/inspiration" className="eyebrow hover:underline">{t("guides")}</Link>
        <h1 className="text-4xl sm:text-5xl my-4">{data.title}</h1>
        <p className="text-lg leading-relaxed">{data.body}</p>
        <Link to="/products" className="btn-primary mt-10">{t("shop_furniture")}</Link>
      </div>
    </article>
  );
};

export const Faq = () => {
  const { t, lang } = useI18n();
  const { data } = useFetch(`/faq?lang=${lang}`, { cacheable: true });
  return (
    <div className="container-hw py-12 max-w-3xl" data-testid="faq-page">
      <Seo title={t("faq")} jsonLd={data && { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: data.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }} />
      <h1 className="text-4xl sm:text-5xl mb-8">{t("faq")}</h1>
      <Accordion type="single" collapsible>{(data || []).map((f) => <AccordionItem key={f.q} value={f.q}><AccordionTrigger className="text-left">{f.q}</AccordionTrigger><AccordionContent>{f.a}</AccordionContent></AccordionItem>)}</Accordion>
      <div className="grid sm:grid-cols-3 gap-3 mt-10 text-sm">{[["/shipping", "shipping_page"], ["/returns", "returns_page"], ["/assembly", "assembly_page"], ["/care", "care_page"], ["/warranty", "warranty_page"], ["/track-order", "track_order"]].map(([to, k]) => <Link key={to} to={to} className="border border-[color:var(--hw-line)] p-4 bg-white hover:border-[color:var(--hw-ink)]">{t(k)}</Link>)}</div>
    </div>
  );
};

export const Contact = () => {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "", order_number: "" });
  const [errs, setErrs] = useState({});
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 2) er.name = t("required");
    if (!/^\S+@\S+\.\S+$/.test(f.email)) er.email = t("invalid_email");
    if (f.subject.trim().length < 2) er.subject = t("required");
    if (f.message.trim().length < 10) er.message = t("required");
    setErrs(er);
    if (Object.keys(er).length) return;
    try { await api.post("/support/tickets", { ...f, order_number: f.order_number || null }); setDone(true); } catch { setErrs({ form: t("error") }); }
  };
  const field = (k, label, area) => (
    <div><label className="label" htmlFor={k}>{label}</label>
      {area ? <textarea id={k} className={`field h-36 py-2 ${errs[k] ? "field-err" : ""}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contact-${k}-input`} />
        : <input id={k} className={`field ${errs[k] ? "field-err" : ""}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contact-${k}-input`} />}
      {errs[k] && <p className="text-xs text-[color:var(--hw-sale)] mt-1" role="alert">{errs[k]}</p>}</div>
  );
  return (
    <div className="container-hw py-12 grid lg:grid-cols-2 gap-14" data-testid="contact-page">
      <Seo title={t("contact")} />
      <div><h1 className="text-4xl sm:text-5xl mb-4">{t("support")}</h1><p className="text-[color:var(--hw-muted)] mb-6">{t("trust_support_d")}</p>
        <ul className="space-y-2 text-sm">{[["/faq", "faq"], ["/track-order", "track_order"], ["/shipping", "shipping_page"], ["/returns", "returns_page"], ["/assembly", "assembly_page"], ["/care", "care_page"], ["/warranty", "warranty_page"]].map(([to, k]) => <li key={to}><Link to={to} className="link-u">{t(k)}</Link></li>)}</ul></div>
      {done ? <p className="text-lg" data-testid="contact-success">{t("message_sent")}</p> : (
        <form onSubmit={submit} noValidate className="space-y-4">
          {field("name", t("name"))}{field("email", t("email"))}{field("order_number", t("order_number_opt"))}{field("subject", t("subject"))}{field("message", t("message"), true)}
          {errs.form && <p className="text-sm text-[color:var(--hw-sale)]">{errs.form}</p>}
          <button className="btn-primary" data-testid="contact-submit-button">{t("send")}</button>
        </form>
      )}
    </div>
  );
};

export const TrackOrder = () => {
  const { t } = useI18n();
  const [f, setF] = useState({ number: "", email: "" });
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const submit = async (e) => { e.preventDefault(); setErr(""); try { const { data } = await api.post("/orders/track", f); setO(data); } catch { setErr(t("not_found")); } };
  return (
    <div className="container-hw py-12 max-w-3xl" data-testid="track-order-page">
      <Seo title={t("track_order")} noindex />
      <h1 className="text-4xl sm:text-5xl mb-3">{t("track_order")}</h1><p className="text-sm text-[color:var(--hw-muted)] mb-6">{t("track_d")}</p>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mb-10">
        <input className="field" placeholder={t("order_number")} value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="track-number-input" required />
        <input className="field" type="email" placeholder={t("email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="track-email-input" required />
        <button className="btn-primary" data-testid="track-submit-button">{t("check")}</button>
      </form>
      {err && <p className="text-sm text-[color:var(--hw-sale)]" role="alert" data-testid="track-error">{err}</p>}
      {o && <OrderView o={o} />}
    </div>
  );
};
